import { Arrow, CustomActionButton, DraggableContainer, Tooltip } from '../Ant';
import { ArrowSquareDown, ArrowSquareUp, QuickActionIcon } from '../icons';
import ClosableModal from '../closableModal/closableModal';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useClearCreateContexts } from '../../hooks/useClearCreateContexts';
import { useFeatureContext } from '../../context/FeatureContext';
import { useDraggableSideButton } from '../../hooks/useDraggableSideButton';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsEvent, FeatureButtons } from '../../interface/analytics';
import { useFetchUserParameters } from '../../hooks/useFetchUserParameters';
import { useSystemInfo } from '../../context/SystemInfoContext';
import { ProductName } from '../../interface/system';
import { ParameterEntityType } from '../../interface/common';
import { useRestrictedUserMatterPermissions } from '../../hooks/useRestrictedUserMatterPermissions';
import {
  QUICK_ACTION_GROUPS,
  QuickActionItem,
  resolveVisibleQuickActionGroups,
} from './quickActionGroups';
import QuickActionMenu, { MENU_ITEM_SELECTOR } from './quickActionMenu';

const QuickAction = () => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const clearCreateContexts = useClearCreateContexts();
  const { data: parametersDetails } = useFetchUserParameters({
    parametersToGet: ParameterEntityType.FEATURES_IN_USE,
  });
  const { systemParams } = useSystemInfo();
  const { canCreateMatterRequests } = useRestrictedUserMatterPermissions();
  const { features } = useFeatureContext();
  const { trackAction } = useAnalytics();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeQuickActions = () => setIsMenuOpen(false);

  const openQuickActions = () => {
    setIsMenuOpen(true);
    trackAction(AnalyticsEvent.BUTTON_CLICK, FeatureButtons.QUICK_ACTIONS);
  };

  const { position, isDragging, dragHandlers } = useDraggableSideButton(
    'button1',
    openQuickActions,
  );

  const isMatterRequestEnabled =
    parametersDetails?.parameters?.featuresInUseParameters?.matterRequestInUse === true &&
    systemParams?.systemInfo?.productName !== ProductName.LAKIMIES &&
    canCreateMatterRequests;

  const isActionVisible = (action: QuickActionItem) =>
    features[action.featureKey] === true &&
    (!action.requiresMatterRequests || isMatterRequestEnabled);

  const visibleGroups = resolveVisibleQuickActionGroups(QUICK_ACTION_GROUPS, isActionVisible);

  const startQuickAction = ({ route }: QuickActionItem) => {
    clearCreateContexts();
    setIsMenuOpen(false);
    push(route);
  };

  const focusFirstMenuItem = (open: boolean) => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEM_SELECTOR)?.focus();
  };

  return (
    <>
      <DraggableContainer
        data-floating-action
        $position={position}
        $isDragging={isDragging}
        {...dragHandlers}
      >
        {isDragging && (
          <>
            <Arrow direction="up">
              <ArrowSquareUp />
            </Arrow>
            <Arrow direction="down">
              <ArrowSquareDown />
            </Arrow>
          </>
        )}
        <Tooltip title={t('common:bottomNavigation.quickaction')} arrow={false} placement="right">
          <CustomActionButton
            type="primary"
            className={isDragging ? 'shaking' : ''}
            aria-label={t('common:bottomNavigation.quickaction')}
            aria-haspopup="dialog"
          >
            <QuickActionIcon />
          </CustomActionButton>
        </Tooltip>
      </DraggableContainer>

      <ClosableModal
        title={t('common:quickActionItems.title')}
        className="quick-action-modal"
        data-testid="quick-action-modal"
        open={isMenuOpen}
        onClose={closeQuickActions}
        afterOpenChange={focusFirstMenuItem}
      >
        <QuickActionMenu ref={menuRef} groups={visibleGroups} onSelect={startQuickAction} />
      </ClosableModal>
    </>
  );
};

export default QuickAction;
